import { FunctionComponent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { faPen, faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";

import { format } from "date-fns";

import trashIcon from "@/assets/icons/trashIcon.svg?url";

import { Icon, LoadingOverlay, Modal, PermissionCheck } from "@/components";

import { useTableSearch } from "@/utils";

import { PermissionRoles } from "@/enum";

import { useDeleteColumnTemplateMutation } from "@/pages/Private/redux/column/column.api";

import { Table, TableColumn } from "@/components/Table/Table";

import { ButtonColor } from "@/components/Button/types";

import { ColumnTemplate } from "@/pages/Private/pages/List/schema/column-template";

import { useGetColumnTemplatesWithAiSnippetOrAiColumnsQuery } from "@/pages/Private/redux/list/list.api";
import { convertHtmlToLinkedInText } from "@/pages/Private/pages/List/components/CampaignOverview/components/Lead/utils/convertHtmlToLinkedin.util";

import { CustomColumnTypes } from "@/enum/list.enum";
import {
	getSuggestions,
	replaceWithRealValues,
} from "@/pages/Private/pages/List/utils/column-utils";

import { userInitials } from "@/utils/userName";

import { UpdatePromptModal } from "./UpdatePromptModal";
import { UpdateAIColumn } from "./UpdateAIColumn";

export const PromptsTable: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`templates.prompts.${key}`), [t]);

	const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
	const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
	const [selectedPrompt, setSelectedPrompt] = useState<ColumnTemplate | null>(null);
	const { page, setPage, limit, setLimit } = useTableSearch();
	const [deleteColumnTemplate, { isLoading }] = useDeleteColumnTemplateMutation();

	const { data, isFetching } = useGetColumnTemplatesWithAiSnippetOrAiColumnsQuery({
		page,
		limit,
	});

	const handleDeleteModal = (row: ColumnTemplate) => {
		setShowDeleteModal(true);
		setSelectedPrompt(row);
	};

	const handleDeletePrompt = async () => {
		if (selectedPrompt?.id) {
			await deleteColumnTemplate(selectedPrompt.id).unwrap();
			setShowDeleteModal(false);
			setSelectedPrompt(null);
		}
	};

	const columns: TableColumn[] = useMemo(
		() => [
			{
				title: ts("name"),
				uid: "name",
				maxWidth: "150px",
				render: (row: ColumnTemplate) => <span className="font-medium">{row.name}</span>,
			},
			{
				title: ts("category"),
				uid: "category",
				render: (row: ColumnTemplate) => (
					<span>{row.type === CustomColumnTypes.AI_PROMPT ? "AI Column" : "AI Snippet"}</span>
				),
			},
			{
				title: ts("prompt"),
				uid: "prompt",
				render: (row: ColumnTemplate) => (
					<div className="max-w-[250px] overflow-hidden text-ellipsis whitespace-nowrap">
						{row.type === CustomColumnTypes.AI_PROMPT
							? replaceWithRealValues(JSON.parse(row.config), null, getSuggestions()).replaceAll(
									"\n",
									" "
								)
							: convertHtmlToLinkedInText(row.config.prompt)}
					</div>
				),
			},
			{
				title: ts("createdBy"),
				uid: "createdBy",
				render: (row: ColumnTemplate) => (
					<div className="flex flex-row w-fit items-center">
						<div className="flex text-xs mr-2 text-white min-w-[26px] w-[26px] max-w-[26px] h-[26px] items-center justify-center rounded-full bg-brand-light p-1 cursor-pointer">
							{userInitials(row.createdBy)}
						</div>
						<div>{row.createdAt ? format(row.createdAt, "HH:mm dd.MM.yyyy") : ""}</div>
					</div>
				),
			},
			{
				title: ts("lastModifiedBy"),
				uid: "updatedBy",
				render: (row: ColumnTemplate) => (
					<div className="flex">
						{row.updatedBy && (
							<div className="flex flex-row w-fit items-center">
								<div className="flex text-xs mr-2 text-white min-w-[26px] w-[26px] max-w-[26px] h-[26px] items-center justify-center rounded-full bg-brand-light p-1 cursor-pointer">
									{userInitials(row.updatedBy)}
								</div>
								<div>{row.updatedAt ? format(row.updatedAt, "HH:mm dd.MM.yyyy") : ""}</div>
							</div>
						)}
					</div>
				),
			},
			{
				title: "",
				uid: "actions",
				render: (row: ColumnTemplate) => (
					<div className="flex items-center gap-2 justify-end">
						<PermissionCheck requiredPermissions={[PermissionRoles.TEMPLATES]}>
							<button
								className="cursor-pointer"
								onClick={() => {
									setSelectedPrompt(row);
									setShowUpdateModal(true);
								}}
							>
								<Icon className="text-text-meta w-4 h-4" icon={faPen} size="lg" />
							</button>
						</PermissionCheck>
						<PermissionCheck requiredPermissions={[PermissionRoles.TEMPLATES]}>
							<button className="cursor-pointer" onClick={() => handleDeleteModal(row)}>
								<img alt="trash" src={trashIcon} />
							</button>
						</PermissionCheck>
					</div>
				),
			},
		],
		[ts, handleDeleteModal]
	);

	const handleChangeRowsPerPage = (newLimit: number) => {
		setLimit(newLimit);
		setPage(1);
	};

	return (
		<div className="flex flex-col gap-4 w-full">
			{isFetching && <LoadingOverlay />}
			<div className="w-full">
				<div className="w-full">
					<Table
						columns={columns}
						data={data?.data ?? []}
						handleChangeRowsPerPage={handleChangeRowsPerPage}
						handleFetchPage={(page) => {
							setPage(page);
						}}
						hidePagination={false}
						limit={limit}
						overallCount={data?.meta?.itemCount}
						page={page}
						sticky={false}
						totalPages={data?.meta?.pageCount}
					/>
				</div>
			</div>
			{showDeleteModal && (
				<Modal
					handleClose={() => setShowDeleteModal(false)}
					handleSave={handleDeletePrompt}
					isLoading={isLoading}
					isOpened={showDeleteModal}
					submitButtonColor={ButtonColor.DANGER}
					submitButtonText={"Delete"}
					title={"Delete Prompt Template"}
				>
					<div className="flex flex-row gap-4 items-start p-2 justify-start text-sm mb-2 text-text border border-status-warning rounded-md">
						<Icon className="text-status-warning w-4 h-4" icon={faTriangleExclamation} />
						You will be unable to use this prompt template in your lists.
					</div>
				</Modal>
			)}
			{showUpdateModal &&
				selectedPrompt &&
				selectedPrompt.type === CustomColumnTypes.AI_SNIPPET && (
					<UpdatePromptModal
						isOpen={showUpdateModal}
						prompt={selectedPrompt}
						setIsOpen={setShowUpdateModal}
					/>
				)}
			{showUpdateModal && selectedPrompt && selectedPrompt.type === CustomColumnTypes.AI_PROMPT && (
				<UpdateAIColumn
					column={selectedPrompt}
					open={showUpdateModal}
					setOpen={setShowUpdateModal}
				/>
			)}
		</div>
	);
};
